import React, { useState, useRef, useEffect } from 'react';
import './OnboardingSlides.css';

const SLIDES = [
  { id: 'welcome', renderer: 'WelcomeSlide' },
  { id: 'zodiac',  renderer: 'ZodiacSlide' },
  { id: 'how',     renderer: 'HowItWorksSlide' },
  { id: 'tilak',   renderer: 'TilakSlide' }
];

const OnboardingSlides = ({ onComplete }) => {
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const goNext = () => {
    if (current < SLIDES.length - 1) setCurrent(c => c + 1);
    else onComplete();
  };

  const goPrev = () => {
    if (current > 0) setCurrent(c => c - 1);
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    const minSwipe = 50;
    if (distance > minSwipe) goNext();
    else if (distance < -minSwipe) goPrev();
    touchStartX.current = null;
    touchEndX.current = null;
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [current]);

  return (
    <div
      className="vtd-onb"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Skip button (slides 0-2 only) */}
      {current < SLIDES.length - 1 && (
        <button
          className="vtd-onb__skip"
          onClick={onComplete}
          aria-label="Skip onboarding"
        >
          Skip
        </button>
      )}

      {/* Slide container with horizontal slide animation.
          Track is 400% wide (one viewport per slide), so each step shifts by 25%
          of the track = one viewport width. */}
      <div
        className="vtd-onb__track"
        style={{ transform: `translateX(-${current * 25}%)` }}
      >
        <WelcomeSlide active={current === 0} />
        <ZodiacSlide active={current === 1} />
        <HowItWorksSlide active={current === 2} />
        <TilakSlide active={current === 3} onComplete={onComplete} />
      </div>

      {/* Bottom dot indicators */}
      <div className="vtd-onb__dots">
        {SLIDES.map((s, i) => (
          <button
            key={s.id}
            className={`vtd-onb__dot${i === current ? ' vtd-onb__dot--active' : ''}`}
            onClick={() => setCurrent(i)}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Next button on slides 0-2 (last slide has its own CTA inside) */}
      {current < SLIDES.length - 1 && (
        <button
          className="vtd-onb__next"
          onClick={goNext}
          aria-label="Next slide"
        >
          Next →
        </button>
      )}
    </div>
  );
};

/* ─── Slide 1: Welcome / Brand ────────────────────────────── */
const WelcomeSlide = ({ active }) => (
  <div className={`vtd-onb__slide vtd-onb__slide--welcome${active ? ' vtd-onb__slide--active' : ''}`}>
    <div className="vtd-onb__logo-wrap">
      <SunLogo />
    </div>
    <h1 className="vtd-onb__title">VTD</h1>
    <p className="vtd-onb__subtitle">Vision Towards Destiny</p>
    <p className="vtd-onb__tagline">Ancient Wisdom. Modern Clarity.</p>
  </div>
);

/* ─── Slide 2: Zodiac Wheel ───────────────────────────────── */
const ZodiacSlide = ({ active }) => (
  <div className={`vtd-onb__slide vtd-onb__slide--zodiac${active ? ' vtd-onb__slide--active' : ''}`}>
    {/* Constellation dots in background */}
    <div className="vtd-onb__stars">
      {[...Array(40)].map((_, i) => (
        <span
          key={i}
          className="vtd-onb__star"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 4}s`,
            animationDuration: `${2 + Math.random() * 3}s`
          }}
        />
      ))}
    </div>

    <ZodiacWheelSVG />
    <h2 className="vtd-onb__title vtd-onb__title--smaller">Your stars have a story.</h2>
    <p className="vtd-onb__subtitle">We read it.</p>
  </div>
);

/* ─── Slide 3: How It Works ───────────────────────────────── */
const HowItWorksSlide = ({ active }) => {
  const steps = [
    { num: 1, text: 'Share your birth details', icon: '📅' },
    { num: 2, text: 'Get your chart read',      icon: '🔮' },
    { num: 3, text: 'Receive your remedies',    icon: '🪔' }
  ];

  return (
    <div className={`vtd-onb__slide vtd-onb__slide--how${active ? ' vtd-onb__slide--active' : ''}`}>
      <h2 className="vtd-onb__title vtd-onb__title--smaller">How It Works</h2>
      <div className="vtd-onb__steps">
        {steps.map((s, i) => (
          <div
            key={s.num}
            className="vtd-onb__step"
            style={{ animationDelay: active ? `${0.3 + i * 0.25}s` : '0s' }}
          >
            <div className="vtd-onb__step-icon">
              <span>{s.icon}</span>
              <span className="vtd-onb__step-check">✓</span>
            </div>
            <div className="vtd-onb__step-num">Step {s.num}</div>
            <div className="vtd-onb__step-text">{s.text}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Slide 4: A Word from Tilak ─────────────────────────── */
const TilakSlide = ({ active, onComplete }) => (
  <div className={`vtd-onb__slide vtd-onb__slide--tilak${active ? ' vtd-onb__slide--active' : ''}`}>
    <h2 className="vtd-onb__title vtd-onb__title--smaller">A Word from Tilak</h2>

    <div className="vtd-onb__tilak-photo">
      <SunLogo small />
    </div>

    <blockquote className="vtd-onb__quote">
      "Astrology to me is not a business — it is a sacred trust passed down through
      three generations of my family. Every reading I give carries that
      responsibility. Welcome to VTD."
    </blockquote>

    <button className="vtd-onb__cta" onClick={onComplete}>
      Begin My Journey →
    </button>
  </div>
);

/* ─── VTD Sun+Rays Logo (standardized) ────────────────────── */
const SunLogo = ({ small }) => (
  <svg
    viewBox="0 0 40 40"
    width={small ? 80 : 140}
    height={small ? 80 : 140}
    xmlns="http://www.w3.org/2000/svg"
    className="vtd-onb__sun-logo"
  >
    <line x1="32"    y1="20"    x2="37"    y2="20"    stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="28.49" y1="28.49" x2="32.02" y2="32.02" stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="20"    y1="32"    x2="20"    y2="37"    stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="11.51" y1="28.49" x2="7.98"  y2="32.02" stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="8"     y1="20"    x2="3"     y2="20"    stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="11.51" y1="11.51" x2="7.98"  y2="7.98"  stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="20"    y1="8"     x2="20"    y2="3"     stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="28.49" y1="11.51" x2="32.02" y2="7.98"  stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round"/>
    <circle cx="20" cy="20" r="9" fill="#D4AF37"/>
    <circle cx="20" cy="20" r="5" fill="#FFD700"/>
    <circle cx="20" cy="20" r="2" fill="#FFF8C0" opacity="0.7"/>
  </svg>
);

/* ─── Zodiac wheel SVG for slide 2 ─────────────────────────── */
const ZodiacWheelSVG = () => {
  const signs = ['♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓'];
  return (
    <div className="vtd-onb__wheel-wrap">
      <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="vtd-onb__wheel">
        {/* Outer ring */}
        <circle cx="100" cy="100" r="95" fill="none" stroke="#D4AF37" strokeWidth="1.5" opacity="0.8"/>
        <circle cx="100" cy="100" r="75" fill="none" stroke="#D4AF37" strokeWidth="0.8" opacity="0.5"/>
        <circle cx="100" cy="100" r="40" fill="none" stroke="#FFD700" strokeWidth="0.8" opacity="0.6"/>

        {/* Center sun */}
        <circle cx="100" cy="100" r="14" fill="#D4AF37"/>
        <circle cx="100" cy="100" r="8"  fill="#FFD700"/>

        {/* Zodiac signs around the wheel */}
        {signs.map((sign, i) => {
          const angle = (i * 30 - 90) * (Math.PI / 180);
          const x = 100 + 85 * Math.cos(angle);
          const y = 100 + 85 * Math.sin(angle);
          return (
            <text
              key={i}
              x={x}
              y={y + 5}
              textAnchor="middle"
              fontSize="13"
              fill="#FFD700"
              fontFamily="Georgia, serif"
            >
              {sign}
            </text>
          );
        })}

        {/* Spoke lines dividing the wheel */}
        {signs.map((_, i) => {
          const angle = (i * 30 - 90) * (Math.PI / 180);
          const x1 = 100 + 40 * Math.cos(angle);
          const y1 = 100 + 40 * Math.sin(angle);
          const x2 = 100 + 75 * Math.cos(angle);
          const y2 = 100 + 75 * Math.sin(angle);
          return (
            <line
              key={i}
              x1={x1} y1={y1} x2={x2} y2={y2}
              stroke="#D4AF37" strokeWidth="0.5" opacity="0.4"
            />
          );
        })}
      </svg>
    </div>
  );
};

export default OnboardingSlides;